/**
 * Plan-tier gating helpers for the dashboard.
 *
 * This mirrors lib/plan_limits.py on the backend — the backend is the
 * authority and rejects over-limit requests; this is purely for showing locks
 * and upgrade prompts before the user hits a 403.
 */

export const PLAN_RANK = {
  standard: 0,
  professional: 1,
};

export const PLAN_LABEL = {
  standard: "Standard",
  professional: "Professional",
};

/** Minimum plan required for each gated feature. */
export const FEATURE_MIN_PLAN = {
  interval_60s: "standard",
  interval_30s: "standard",
  sms_alerts: "professional",
  voice_alerts: "professional",
  on_call: "professional",
  status_pages: "standard",
  api_keys: "professional",
  groups: "professional",
  rum: "professional",
  servers: "professional",
  operations: "professional",
};

/** Max monitors per plan (null = unlimited). Mirrors the backend. */
export const MAX_MONITORS = { standard: 50, professional: null };

/**
 * Minimum plan for each notification channel type. Mirrors the
 * allowed_channel_types sets in lib/plan_limits.py.
 * SMS and voice are Professional-only (carrier costs); everything else is Standard.
 */
export const CHANNEL_MIN_PLAN = {
  email: "standard",
  slack: "standard",
  discord: "standard",
  telegram: "standard",
  webhook: "standard",
  msteams: "standard",
  pagerduty: "standard",
  opsgenie: "standard",
  bitrix24: "standard",
  sms: "professional",
  voice: "professional",
};

/** True if `plan` may use notification channel `type`. */
export function canUseChannel(type, plan) {
  const required = CHANNEL_MIN_PLAN[type] ?? "professional";
  return PLAN_RANK[plan] >= PLAN_RANK[required];
}

/** True if `plan` is allowed to use `feature`. */
export function canAccess(feature, plan) {
  const required = FEATURE_MIN_PLAN[feature];
  if (required === undefined) return true;
  return PLAN_RANK[plan] >= PLAN_RANK[required];
}

/** The minimum plan name that unlocks `feature`, or null if always available. */
export function minPlanFor(feature) {
  return FEATURE_MIN_PLAN[feature] ?? null;
}

/** Human label for the minimum plan that unlocks `feature` (e.g. "Professional"). */
export function minPlanLabelFor(feature) {
  const plan = minPlanFor(feature);
  return plan ? PLAN_LABEL[plan] : null;
}
